import cx from 'classnames';
import React from 'react';
import { connect } from 'react-redux';
import { yieldCallback } from '@zillow/yield-callback';
import type { RootReduxState } from 'app/shared/flux/reducers';
import type { AppState } from 'app/shared/flux/reducers/app';
import './style.scss';

interface TooltipOwnProps {
  children?: React.ReactNode;
  tip?: React.ReactNode;
  tooltipPosition?: 'above' | 'below';
  width?: string;
  className?: string;
}

interface TooltipStateProps {
  device: AppState['device'];
}

type TooltipProps = TooltipOwnProps & TooltipStateProps;

interface TooltipState {
  showTooltip: boolean;
  adjustedPosition?: 'above' | 'below';
  horizontalOffset?: number;
  arrowOffset?: number;
  positionCalculated?: boolean;
}

class Tooltip extends React.Component<TooltipProps, TooltipState> {
  static defaultProps: Partial<TooltipProps> = {
    children: null,
    tip: null,
    tooltipPosition: 'above',
  };

  private tooltipRef = React.createRef<HTMLSpanElement>();
  private triggerRef = React.createRef<HTMLSpanElement>();
  private tooltipContentRef = React.createRef<HTMLSpanElement>();

  constructor(props: TooltipProps) {
    super(props);
    this.state = {
      showTooltip: false,
      adjustedPosition: undefined,
      horizontalOffset: undefined,
      arrowOffset: undefined,
      positionCalculated: false,
    };
  }

  componentDidMount() {
    window.addEventListener('click', this.handleClickCloseTooltip);
    window.addEventListener('touchstart', this.handleClickCloseTooltip, { passive: true });
  }

  componentWillUnmount(): void {
    window.removeEventListener('click', this.handleClickCloseTooltip);
    window.removeEventListener('touchstart', this.handleClickCloseTooltip);
  }

  componentDidUpdate(prevProps: TooltipProps, prevState: TooltipState) {
    if (!prevState.showTooltip && this.state.showTooltip) {
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        this.calculatePosition();
      });
    }
  }

  calculatePosition = () => {
    const tooltip = this.tooltipContentRef.current;
    const trigger = this.triggerRef.current;
    if (!tooltip || !trigger) return;

    const { tooltipPosition } = this.props;
    const tooltipRect = tooltip.getBoundingClientRect();
    const triggerRect = trigger.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    // Find the nearest scrollable container (modal content area)
    let scrollContainer = trigger.parentElement;
    let containerBounds = null;
    while (scrollContainer) {
      const style = window.getComputedStyle(scrollContainer);
      const overflow = style.overflow + style.overflowY + style.overflowX;
      if (
        overflow.includes('auto') ||
        overflow.includes('scroll') ||
        scrollContainer.classList.contains('PopupModal-content')
      ) {
        containerBounds = scrollContainer.getBoundingClientRect();
        break;
      }
      scrollContainer = scrollContainer.parentElement;
    }

    // Use container bounds if found, otherwise use viewport
    const rightBoundary = containerBounds ? containerBounds.right : viewportWidth;
    const leftBoundary = containerBounds ? containerBounds.left : 0;

    let adjustedPosition = tooltipPosition;
    let horizontalOffset = 0;
    let arrowOffset = 0;

    // Check vertical positioning
    if (tooltipPosition === 'above') {
      const spaceAbove = triggerRect.top;
      const tooltipHeight = tooltipRect.height;

      if (spaceAbove < tooltipHeight + 8) {
        adjustedPosition = 'below';
      }
    } else {
      const spaceBelow = viewportHeight - triggerRect.bottom;
      const tooltipHeight = tooltipRect.height;

      if (spaceBelow < tooltipHeight + 8) {
        adjustedPosition = 'above';
      }
    }

    // Check horizontal positioning against container bounds
    const tooltipLeft = triggerRect.left - 10; // Account for margin-left: -10px in CSS
    const tooltipRight = tooltipLeft + tooltipRect.width;

    if (tooltipLeft < leftBoundary) {
      // Tooltip extends past left edge
      horizontalOffset = leftBoundary - tooltipLeft + 20; // Add padding from edge
      arrowOffset = -horizontalOffset;
    } else if (tooltipRight > rightBoundary - 20) {
      // Tooltip extends past right edge - check with padding
      horizontalOffset = rightBoundary - tooltipRight - 20; // Add padding from edge
      arrowOffset = -horizontalOffset;
    }

    // Update state with calculated position
    this.setState({
      adjustedPosition,
      horizontalOffset,
      arrowOffset,
      positionCalculated: true,
    });
  };

  handleClickCloseTooltip = (e: MouseEvent | TouchEvent): void => {
    const { showTooltip } = this.state;

    if (showTooltip && this.tooltipRef.current) {
      // Check if the click/touch was outside the tooltip
      const target = e.target as Node;
      if (!this.tooltipRef.current.contains(target)) {
        this.setState({
          showTooltip: false,
          adjustedPosition: undefined,
          horizontalOffset: undefined,
          arrowOffset: undefined,
          positionCalculated: false,
        });
      }
    }
  };

  yieldHandleClick = yieldCallback(() => {
    const { showTooltip } = this.state;
    this.setState({
      showTooltip: !showTooltip,
    });
  });

  handleClick = (e: React.MouseEvent<HTMLSpanElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    this.yieldHandleClick();
  };

  handleMouseOver = (): void => {
    const { device } = this.props;
    if (device.deviceType === 'desktop') {
      this.setState({ showTooltip: true });
    }
  };

  handleMouseLeave = (): void => {
    const { device } = this.props;
    if (device.deviceType === 'desktop') {
      this.setState({
        showTooltip: false,
        adjustedPosition: undefined,
        horizontalOffset: undefined,
        arrowOffset: undefined,
        positionCalculated: false,
      });
    }
  };

  render(): React.ReactNode {
    const { tip, children, tooltipPosition, width, className } = this.props;
    const { showTooltip, adjustedPosition, horizontalOffset, arrowOffset, positionCalculated } = this.state;

    const effectivePosition = adjustedPosition || tooltipPosition;
    const shouldShowTooltip = showTooltip && positionCalculated;

    return (
      <span
        ref={this.tooltipRef}
        className={cx('Tooltip', className)}
        onClick={this.handleClick}
        onMouseLeave={this.handleMouseLeave}
        onMouseOver={this.handleMouseOver}
      >
        {showTooltip && (
          <span style={{ visibility: shouldShowTooltip ? 'visible' : 'hidden' }}>
            <span
              className={cx('Tooltip-arrow', {
                'Tooltip-arrow-above': effectivePosition === 'above',
                'Tooltip-arrow-below': effectivePosition === 'below',
              })}
              style={
                arrowOffset
                  ? {
                      left: `${arrowOffset}px`,
                      marginLeft: 0,
                    }
                  : undefined
              }
            />
            <span
              ref={this.tooltipContentRef}
              style={{
                width: width || '200px',
                ...(horizontalOffset
                  ? {
                      left: `${horizontalOffset}px`,
                      marginLeft: '-10px',
                    }
                  : {}),
              }}
              className={cx('Tooltip-tip', {
                'Tooltip-tip-above': effectivePosition === 'above',
                'Tooltip-tip-below': effectivePosition === 'below',
              })}
            >
              {tip}
            </span>
          </span>
        )}
        <span ref={this.triggerRef} className="Tooltip-children">
          {children}
        </span>
      </span>
    );
  }
}

const mapStateToProps = (state: RootReduxState): TooltipStateProps => ({
  device: state.app.device,
});

export default connect(mapStateToProps)(Tooltip);
